import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "../components/PageShell";
import { Figure } from "../components/Figure";
import { eventTimestamp, type EventItem } from "../lib/events";
import { usePublishedEvents } from "../lib/publishedEvents";
import { ROUTES } from "../lib/routes";

const ALL = "All";

/**
 * The events index: a chronological timeline grouped by year, with a category
 * filter — deliberately a different shape from /news (featured story +
 * pagination). An event is a date before it is a story, so the date block
 * leads every row; the filter uses the same WAI-ARIA tabs pattern as the
 * Visitors and Alumni filters.
 *
 * `items` must already be newest-first; grouping preserves that order.
 */
function byYear(items: EventItem[]) {
  const groups: Array<{ year: string; items: EventItem[] }> = [];
  for (const e of items) {
    const year = e.dateBig.year;
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.items.push(e);
    else groups.push({ year, items: [e] });
  }
  return groups;
}

export default function Events() {
  const [active, setActive] = useState<string>(ALL);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const published = usePublishedEvents();

  // Sorted because a published event isn't guaranteed to arrive newest-first.
  const allEvents = useMemo(
    () => [...published].sort((a, b) => eventTimestamp(b) - eventTimestamp(a)),
    [published],
  );

  /** Categories derived from the data, in order of first appearance. */
  const TAGS = useMemo(
    () => [ALL, ...allEvents.reduce<string[]>((acc, e) => (acc.includes(e.tag) ? acc : [...acc, e.tag]), [])],
    [allEvents],
  );

  const tabs = TAGS.map((t) => ({
    key: t,
    count: t === ALL ? allEvents.length : allEvents.filter((e) => e.tag === t).length,
  }));

  const years = useMemo(
    () => byYear(active === ALL ? allEvents : allEvents.filter((e) => e.tag === active)),
    [active, allEvents],
  );

  // Roving-focus keyboard nav across the tab strip (WAI-ARIA tabs pattern).
  function onTabKeyDown(e: React.KeyboardEvent, i: number) {
    const delta = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!delta && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next =
      e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : (i + delta + tabs.length) % tabs.length;
    setActive(tabs[next].key);
    tabRefs.current[next]?.focus();
  }

  return (
    <>
      <PageHero
        variant="hero-editorial hero-compact"
        eyebrow="Student Life · Events"
        title="When the campus gathers."
        lead={`Festivals, ceremonies, workshops and guest talks, as the institute's own record has them — ${allEvents.length} events, each on its published date.`}
        crumbs={[{ label: "Events" }]}
        image={null}
        fullWidthTitle
      />

      <section className="section">
        <div className="container">
          <div className="filter-tabs" role="tablist" aria-label="Filter events by category">
            {tabs.map((t, i) => (
              <button
                key={t.key}
                ref={(el) => { tabRefs.current[i] = el; }}
                role="tab"
                type="button"
                id={`ev-tab-${i}`}
                aria-selected={active === t.key}
                aria-controls="ev-panel"
                tabIndex={active === t.key ? 0 : -1}
                className={"filter-tab" + (active === t.key ? " is-active" : "")}
                onClick={() => setActive(t.key)}
                onKeyDown={(e) => onTabKeyDown(e, i)}
              >
                {t.key} <span className="filter-tab-count">{t.count}</span>
              </button>
            ))}
          </div>

          <div id="ev-panel" role="tabpanel" aria-labelledby={`ev-tab-${tabs.findIndex((t) => t.key === active)}`}>
            {years.length === 0 && (
              <p className="dept-thin-note">
                No events published yet. This page now shows only what webadmin has published —
                check back once the first one goes live.
              </p>
            )}
            {years.map((g) => (
              <div key={g.year} className="evi-year reveal">
                <div className="evi-year-head">
                  <span className="evi-year-num">{g.year}</span>
                  <span className="evi-year-count">
                    {g.items.length} event{g.items.length > 1 ? "s" : ""}
                  </span>
                </div>
                <ul className="evi-rows">
                  {g.items.map((e) => (
                    <li key={e.slug}>
                      <Link className="evi-row" to={`${ROUTES.events}/${e.slug}`}>
                        <span className="evi-date" aria-hidden="true">
                          <span className="evi-date-month">{e.dateBig.month}</span>
                          <span className="evi-date-day">{e.dateBig.day}</span>
                        </span>
                        <Figure
                          media={e.media}
                          fallbackLabel={e.hero}
                          aspect="16/10"
                          className="evi-thumb"
                          sizes="180px"
                        />
                        <span className="evi-body">
                          <span className="evi-meta">
                            <span className="event-tag">{e.tag}</span>
                            <span className="evi-venue">{e.venue}</span>
                          </span>
                          <span className="evi-title">{e.title}</span>
                          <span className="evi-summary">{e.summary}</span>
                        </span>
                        <span className="evi-arr" aria-hidden="true">
                          →
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p style={{ marginTop: 48, fontSize: 14, color: "var(--text-muted)" }}>
            Graduation ceremonies, with a photograph album for each, are listed on{" "}
            <Link to={`${ROUTES.campusLife}#graduation`} style={{ color: "var(--accent-strong)" }}>
              Campus Life
            </Link>
            .
          </p>
        </div>
      </section>
    </>
  );
}
